'use client'

import { clsx } from 'clsx'
import { type FC, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  Accordion,
  AccordionDetails,
  AccordionStack,
  AccordionSummary,
} from 'base-ui'

import { MediServicePhoneServiceForm } from '../MediServicePhoneServiceForm/MediServicePhoneServiceForm'
import { MediServiceVideoServiceForm } from '../MediServiceVideoServiceForm/MediServiceVideoServiceForm'

import { MediServiceFurtherInformationEmail } from './MediServiceFurtherInformationEmail'
import { MediServiceFurtherInformationTelephone } from './MediServiceFurtherInformationTelephone'

import styles from './MediServiceFurtherInformation.module.css'

type MediServiceFurtherInformationProps = {
  className?: string
  displayInColumn?: boolean
  overrideContactEmail?: ReactNode
  overrideContactPhone?: ReactNode
}

const MEDI_SERVICE_FURTHER_INFORMATION_ANCHOR_ID = 'further-information-forms'

/* Explanation can be found here app/domain/medi-service/components/Faq/Faq.tsx */
/* eslint-disable react/jsx-max-depth -- the rule was disabled before a comment became required */
export const MediServiceFurtherInformation: FC<
  MediServiceFurtherInformationProps
> = ({
  className,
  displayInColumn = false,
  overrideContactEmail,
  overrideContactPhone,
}) => {
  return (
    <div
      className={clsx(
        'scroll-mt-4 tablet:flex tablet:justify-center',
        className,
      )}
      id={MEDI_SERVICE_FURTHER_INFORMATION_ANCHOR_ID}
    >
      <div
        className={clsx(
          styles['further-information'],
          'container flex-col px-4 tablet:px-0',
        )}
      >
        <span className={styles['further-information-title']}>
          <FormattedMessage id="chrx.further-info.title" />
        </span>
        <div
          className={clsx(
            styles['further-information-container'],
            'flex-col',
            displayInColumn ? 'tablet:flex-col' : 'tablet:flex-row',
          )}
        >
          <div
            className={clsx(
              styles['further-information-column'],
              'flex flex-col items-start gap-6 self-stretch rounded-l bg-light-primary-medium p-6 tablet:gap-6 tablet:self-start tablet:p-6',
              displayInColumn ? 'w-full' : '',
            )}
          >
            <MediServiceFurtherInformationEmail
              overrideContactEmail={overrideContactEmail}
            />
            <MediServiceFurtherInformationTelephone
              overrideContactPhone={overrideContactPhone}
            />
          </div>
          {/* Paddings in collapsibles causing this column to shrink when flex is 1 0 0, hence the hack with hard-coded max-width. */}
          {/* Adjust collapsibles + remove the hard-coded width and set flex to 1 0 0 as in design on further-information-column to fix the issue. */}
          {/* Taken is calculated value from the Figma designs */}
          {/* https://www.figma.com/file/bq2e0obtVfcT7YTJqW92Cq/Rx-Landingpage-for-CH?type=design&node-id=1166-39377&mode=dev */}
          {/* app/domain/medi-service/components/MediServiceFurtherInformation/FurtherInformation.module.css:26 */}
          <div
            // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
            className={clsx(
              styles['further-information-column'],
              'flex flex-col items-start self-stretch tablet:max-w-[472px]',
            )}
          >
            <AccordionStack className="self-stretch">
              <Accordion defaultExpanded>
                <AccordionSummary>
                  <FormattedMessage id="chrx.further-info.phone-service.title" />
                </AccordionSummary>
                <AccordionDetails>
                  <MediServicePhoneServiceForm />
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary>
                  <FormattedMessage id="chrx.further-info.video-service.title" />
                </AccordionSummary>
                <AccordionDetails>
                  <MediServiceVideoServiceForm />
                </AccordionDetails>
              </Accordion>
            </AccordionStack>
          </div>
        </div>
      </div>
    </div>
  )
}
/* eslint-enable react/jsx-max-depth */
